import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { array, object, string } from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  Divider,
  ListItemText,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Label from '../../components/Label';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import useAuth from '../../hooks/useAuth';
import useSettings from '../../hooks/useSettings';
import Iconify from '...components/Iconify';
import { FormProvider, RHFSelect, RHFTextField } from '...components/hook-form';
import { getMetadata } from '...data/common/schemas/_m';
import { RESULT_TYPES, RESULT_TYPES_REGEX } from '...data/results/constants';
import subscriptionsRef from '...data/subscriptions/collections/subscriptions';
import { app } from '...modules/firebase/web';

const firestore = getFirestore(app);

const title = 'Routing';

const updateSubscription = async (id, data) => {
  const ref = doc(firestore, subscriptionsRef.id, id);
  await updateDoc(ref, data);
};

const createSubscription = async (data) => {
  // TODO
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
};

const EmailSection = ({ children }) => (
  <Stack spacing={2}>
    <Typography variant="overline" sx={{ color: 'text.secondary' }}>
      Email
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.primary' }}>
      We&apos;ll send a report to this email address every time we encounter a security
      event. Reports contain links to related signals, anomalies, and identity.
    </Typography>
    {children}
  </Stack>
);

const WebhookSection = ({ children }) => (
  <Stack spacing={2}>
    <Typography variant="overline" sx={{ color: 'text.secondary' }}>
      Webhook URL
    </Typography>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      HTTPS required. Example: https://website.com/dfend-events
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.primary' }}>
      We&apos;ll send JSON data to this URL every time we encounter a security event. Data
      includes findings and recommendations, IPs, locations, and templates with embedded
      links for user verification.
    </Typography>
    {children}
  </Stack>
);

const EventTypesSection = ({ children }) => (
  <Stack spacing={2}>
    <Typography variant="overline" sx={{ color: 'text.secondary' }}>
      Event types
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.primary' }}>
      Configure the events that you receive. This is optional.
    </Typography>
    {children}
  </Stack>
);

const DestinationsSection = ({ children }) => (
  <Stack spacing={3}>
    <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
      Destinations
    </Typography>

    {children}
  </Stack>
);

const SettingsSection = ({ children }) => (
  <Stack spacing={3}>
    <Box sx={{ pt: 1 }}>
      <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
        Settings
      </Typography>
    </Box>
    {children}
  </Stack>
);

const RouteForm = ({ cancel, initValues = {}, id, accountId, isAdmin }) => {
  const { enqueueSnackbar } = useSnackbar();

  const RouteSchema = object({
    email: string().email('Email must be a valid email address'),
    webhookMethod: string()
      .matches(/GET|POST/)
      .optional(),
    webhookUrl: string()
      .url()
      .matches(/^(https:\/\/)?/)
      .optional(),
    resultTypes: array().of(string().matches(RESULT_TYPES_REGEX)) // config.<type>.enabled
  });

  const defaultValues = {
    email: '',
    webhookMethod: 'POST',
    webhookUrl: '',
    resultTypes: Object.keys(RESULT_TYPES),
    ...initValues
  };

  const methods = useForm({
    resolver: yupResolver(RouteSchema),
    defaultValues
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting, isDirty }
  } = methods;

  const onSubmit = async (data) => {
    if (!data.email && !data.webhookUrl) {
      enqueueSnackbar('Error updating routing. Must provide email, webhook, or both.', {
        variant: 'error'
      });
      return;
    }
    if (data.webhookUrl && !data.webhookMethod) {
      enqueueSnackbar('Error updating routing. Webhook must have an HTTP method.', {
        variant: 'error'
      });
      return;
    }
    if (!data.resultTypes.length) {
      enqueueSnackbar('Error updating routing. Must choose at least one result type.', {
        variant: 'error'
      });
      return;
    }
    const subscription = {
      destination: {}
    };
    if (data.email) {
      subscription.destination.emails = [data.email];
    }
    if (data.webhookUrl && data.webhookMethod) {
      subscription.destination.webhook = {
        url: data.webhookUrl,
        method: data.webhookMethod
      };
    }
    if (data.resultTypes) {
      subscription.config = {};
      //console.log('RESULT TYPES', data.resultTypes);
      data.resultTypes.forEach((r) => {
        subscription.config[r] = { enabled: true };
      });
    }

    try {
      console.log('SUBMIT CALLED:', subscription);
      if (id) {
        await updateSubscription(id, subscription);
      } else {
        subscription.account = accountId;
        await createSubscription(subscription);
      }
      enqueueSnackbar('Routing updated.');
      // FIX (Aug 7): cancel was called unconditionally, but RouteForm's one
      // render site (below) never actually passes a cancel prop — this
      // threw on every successful save, landing in the catch block below
      // and showing a false "Error updating routing" toast right after the
      // real success toast. Guarded rather than wiring up a real handler
      // since this form isn't a dismissible dialog (no show/hide state to
      // cancel out of) — see the render site's other props.
      cancel?.();
    } catch (error) {
      enqueueSnackbar('Error updating routing.', {
        variant: 'error'
      });
      console.error(error);
    }
  };

  const resultTypes = watch('resultTypes');

  return (
    <Card sx={{ p: 3 }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={5}>
          <DestinationsSection>
            <Stack spacing={4}>
              <EmailSection>
                <RHFTextField
                  // style={{ marginTop: 24 }}
                  name="email"
                  label="Email address"
                  disabled={!isAdmin}
                />
              </EmailSection>

              <WebhookSection>
                <RHFTextField name="webhookUrl" label="Webhook URL" disabled={!isAdmin} />

                <RHFSelect
                  name="webhookMethod"
                  label="HTTP method"
                  disabled={!isAdmin}
                  selectProps={{
                    native: false,
                    MenuProps
                  }}
                >
                  {['GET', 'POST'].map((type) => (
                    <MenuItem key={type} value={type}>
                      <ListItemText primary={type} />
                    </MenuItem>
                  ))}
                </RHFSelect>
              </WebhookSection>
            </Stack>
          </DestinationsSection>

          <SettingsSection>
            <EventTypesSection>
              <RHFSelect
                name="resultTypes"
                label="Types of events"
                disabled={!isAdmin}
                selectProps={{
                  native: false,
                  multiple: true,
                  MenuProps,
                  renderValue: (selected) => selected.join(', ')
                }}
              >
                {Object.keys(RESULT_TYPES).map((type) => (
                  <MenuItem key={type} value={type}>
                    <Checkbox checked={resultTypes.indexOf(type) > -1} />
                    <ListItemText primary={type} />
                  </MenuItem>
                ))}
              </RHFSelect>
            </EventTypesSection>
          </SettingsSection>

          <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ pt: 1 }}>
            {isDirty && (
              <Button color="inherit" size="small" onClick={() => reset(defaultValues)}>
                Discard changes
              </Button>
            )}

            <LoadingButton
              color="primary"
              size="small"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={!isDirty}
            >
              Save
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Card>
  );
};

export default function Routing() {
  const { themeStretch } = useSettings();
  const { subscriptions, account, user } = useAuth();

  const subscription = subscriptions[0];
  const initValues = {};

  console.log('Subscription:', subscription);

  if (subscription && Object.keys(subscription).length) {
    const emails = subscription.destination?.emails;
    const webhook = subscription.destionation?.webhook;
    const config = subscription.config;

    if (emails && emails[0]) {
      initValues.email = emails[0];
    }
    if (webhook) {
      initValues.webhookMethod = webhook.method || 'POST';
      initValues.webhookUrl = webhook.url || '';
    }
    if (config) {
      const resultTypes = [];
      Object.keys(config).forEach((key) => {
        // Only result types.
        if (RESULT_TYPES[key]) {
          const c = config[key];
          if (c.enabled) {
            resultTypes.push(key);
          }
        }
      });
      initValues.resultTypes = resultTypes;
    }
  }

  return (
    <Page title={`Dashboard | Setup | ${title}`}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <Box sx={{ mb: 5 }}>
          <Typography variant="h4" gutterBottom>
            {title}{' '}
            {user.role !== 'admin' && (
              <Label color="error" sx={{ ml: 1 }}>
                <Iconify icon={'eva:alert-circle-fill'} />
                &nbsp;&nbsp;Must have admin role to make changes
              </Label>
            )}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ mt: 3 }}>
          {!!subscription?.id && (
            <RouteForm
              initValues={initValues}
              id={subscription?.id}
              accountId={account.id}
              isAdmin={user.role === 'admin'}
            />
          )}
        </Box>
      </Container>
    </Page>
  );
}
